'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

type QueryFnParams = {
  page: number;
  size: number;
};

type MetaType = {
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
};

type ResponseType<T, S = any> = {
  data: T[];
  meta: MetaType;
  summary?: S;
  links?: {
    next?: string;
    prev?: string;
    self?: string;
    first?: string;
    last?: string;
  };
};

type Translations = {
  showMoreData: (current: number, total: number) => string;
  loaded: string;
  loadMore: (nextDataLength: number) => string;
  error?: string;
};

export type LoadMoreDataHookProps<T> = {
  queryKey: string[];
  queryFn: (params: QueryFnParams) => Promise<ResponseType<T>>;
  dataItemUniqueID?: keyof T;
  pageSize?: number;
  translations?: Translations;
  onError?: (error: Error) => void;
};

export type LoadMoreDataHookResult<T, S = any> = {
  isInitialLoading: boolean;
  isLoading: boolean;
  isRefetching: boolean;
  data: T[];
  summary?: S;
  loadedPercentage: number;
  handleLoadMoreClick: () => void;
  label: string;
  buttonLabel: string;
  current: number;
  hasNextPage: boolean;
  total: number;
  error: Error | null;
  hasError: boolean;
  refetch: () => void;
  isFetching: boolean;
};

const useLoadMoreData = <T extends Record<string, any>, S = any>({
  queryKey,
  queryFn,
  pageSize = 10,
  translations = {
    showMoreData: (current, total) => `Showing ${current} of ${total}`,
    loaded: 'All items loaded',
    loadMore: (nextDataLength) => `Load ${nextDataLength} more`,
  },
  onError,
}: LoadMoreDataHookProps<T>): LoadMoreDataHookResult<T, S> => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isRefetching, error, refetch, isFetching } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam = 0 }) => queryFn({ page: pageParam, size: pageSize }),
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.links?.next ? allPages?.length : undefined;
      },
      refetchOnMount: true,
      initialPageParam: 0,
    });

  // Flatten all pages data into a single array
  const flattenedData = useMemo(
    () => data?.pages?.reduce<T[]>((acc, page) => [...acc, ...(page?.data || [])], []) || [],
    [data]
  );

  const current = flattenedData.length;

  const total = data?.pages[0]?.meta.totalElements || 0;

  const loadedPercentage = useMemo(() => (total ? Math.min((current / total) * 100, 100) : 0), [current, total]);

  const label = useMemo(() => translations.showMoreData(current, total), [current, total]);

  const buttonLabel = useMemo(
    () =>
      loadedPercentage >= 100
        ? translations.loaded
        : translations.loadMore(total - current < pageSize ? total - current : pageSize),
    [loadedPercentage, total, current, pageSize]
  );

  const handleLoadMoreClick = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  // If there's an error and onError callback is provided, call it
  if (error && onError) {
    onError(error instanceof Error ? error : new Error(String(error)));
  }

  // Get summary from the first page
  const summary = useMemo(() => data?.pages[0]?.summary, [data]);

  return {
    isInitialLoading: isLoading,
    isLoading: isFetchingNextPage,
    isRefetching,
    data: flattenedData,
    summary,
    loadedPercentage,
    handleLoadMoreClick,
    hasNextPage,
    label,
    buttonLabel,
    current,
    total,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    hasError: !!error,
    refetch,
    isFetching,
  };
};

export default useLoadMoreData;
